// The value side of the secret templates: one value or named fields (a database's
// connection, a Jira site with its email and token, D78), how a stored credential's value is
// asked for again, and the plaintext that gets sealed. Labels are getters over the active
// language's messages (D70).
import type { Schemas } from "../api/client";
import { messages } from "../i18n";

export interface FieldSpec {
  /** The field name as stored (`[a-z0-9_]`), also the form input's name. */
  name: string;
  readonly label: string;
  /** Masked while typed; the others are connection details, still sealed and write-only. */
  masked: boolean;
  /** The input's type and example, for fields that are not free text. */
  input?: { type: "url" | "email"; placeholder: string };
}

/**
 * One value, or a JSON object of named fields (the sealed value is then exactly that object),
 * with an optional help line of its own.
 */
export type ValueShape =
  | { kind: "single"; readonly label: string }
  | { kind: "named"; fields: FieldSpec[]; readonly help?: string }
  /** A text file pasted or loaded (a VPN profile, D80): multi-line, at most `maxBytes`. */
  | { kind: "file"; readonly label: string; readonly help: string; accept: string; maxBytes: number };

/** The database's connection fields, in form order. */
export type DatabaseField = "host" | "port" | "user" | "password" | "database";

/** A Jira secret's fields (D78): the broker calls `site` as `email` with `api_token`. */
export type JiraField = "site" | "email" | "api_token";

export type FieldName = DatabaseField | JiraField;

const copy = () => messages().secrets.templates;

export const single = (label: () => string): ValueShape => ({
  kind: "single",
  get label() {
    return label();
  },
});

const field = (name: FieldName, masked: boolean, input?: FieldSpec["input"]): FieldSpec => ({
  name,
  masked,
  ...(input ? { input } : {}),
  get label() {
    return copy().field[name];
  },
});

export const DATABASE_FIELDS: FieldSpec[] = [field("host", false), field("port", false), field("user", false), field("password", true), field("database", false)];

const JIRA_FIELDS: FieldSpec[] = [
  field("site", false, { type: "url", placeholder: "https://acme.atlassian.net" }),
  field("email", false, { type: "email", placeholder: "you@acme.com" }),
  field("api_token", true),
];

export const JIRA_SHAPE: Extract<ValueShape, { kind: "named" }> = {
  kind: "named",
  fields: JIRA_FIELDS,
  get help() {
    return copy().jiraHelp;
  },
};

/** A VPN profile (`.ovpn`, D80): the whole file, sealed as one value. */
export const VPN_PROFILE_SHAPE: Extract<ValueShape, { kind: "file" }> = {
  kind: "file",
  accept: ".ovpn,.conf,text/plain",
  maxBytes: 64 * 1024,
  get label() {
    return copy().value.vpn_profile;
  },
  get help() {
    return copy().vpnHelp;
  },
};

/** A shape's fields by name, for a stored credential's replace form. */
const KNOWN_FIELDS = [...DATABASE_FIELDS, ...JIRA_FIELDS];

/** The value inputs to replace a stored credential's value: its own fields, or one value. */
export function valueShapeOf(credential: Pick<Schemas["CredentialDto"], "fields" | "kind">): ValueShape {
  // A Jira secret always asks for its site, email and token (D78), so one made before them
  // (a single value) is converted in place when its value is replaced.
  if (credential.kind === "jira_token") return JIRA_SHAPE;
  if (credential.kind === "vpn_profile") return VPN_PROFILE_SHAPE;
  if (credential.fields.length === 0) return single(() => copy().newValue);
  const known = (name: string) => KNOWN_FIELDS.find((f) => f.name === name);
  const fields = credential.fields.map((name) => known(name) ?? { name, label: name, masked: true });
  return { kind: "named", fields };
}

/** The field names the API stores: empty for a single value. */
export function fieldNames(shape: ValueShape): string[] {
  return shape.kind === "named" ? shape.fields.map((f) => f.name) : [];
}

/**
 * The plaintext to seal: the value itself, or a JSON object of exactly the named fields.
 * Throws when a part is empty; the caller shows the message.
 */
export function plaintext(shape: ValueShape, read: (name: string) => string): string {
  if (shape.kind === "single") {
    const value = read("value");
    if (value === "") throw new Error(copy().valueEmpty);
    return value;
  }
  if (shape.kind === "file") {
    const value = read("value");
    if (value.trim() === "") throw new Error(copy().valueEmpty);
    if (new TextEncoder().encode(value).length > shape.maxBytes) throw new Error(copy().fileTooLarge(shape.maxBytes / 1024));
    return value;
  }
  const parts: Record<string, string> = {};
  for (const f of shape.fields) {
    const value = read(f.name).trim();
    if (value === "") throw new Error(copy().fieldEmpty(f.label));
    parts[f.name] = f.input?.type === "url" ? siteOrigin(value) : value;
  }
  return JSON.stringify(parts);
}

/**
 * A Jira site as the origin the broker calls (`https://acme.atlassian.net`): any path, query
 * or trailing slash dropped. Throws for anything but an http(s) URL with a host; the broker
 * decides which sites are allowed (Jira Cloud, or the operator's list).
 */
export function siteOrigin(text: string): string {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    throw new Error(copy().siteInvalid);
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.hostname === "" || url.username !== "" || url.password !== "") {
    throw new Error(copy().siteInvalid);
  }
  return url.origin;
}
